import { AwaitContainer } from '@ibsys/kit';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { IntegrationListItem, useIntegrations } from '@/entities/integrations';
import { PageContainer } from '@/shared/ui';

import { EditIntegrationDialog } from './EditIntegrationDialog';
import { IntegrationsTable } from './IntegrationsTable';

const PER_PAGE = 20;

export const IntegrationsAllPage: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => +(searchParams.get('page') || 1));
  const [editing, setEditing] = useState<IntegrationListItem | null>(null);

  const { data, isLoading } = useIntegrations({
    limit: PER_PAGE,
    offset: (page - 1) * PER_PAGE,
  });

  const handleChangePagination = useCallback(
    (nextPage: number) => {
      setPage(nextPage);
      setSearchParams((prev) => {
        prev.set('page', nextPage.toString());
        return prev;
      });
    },
    [setSearchParams],
  );

  const handleEdit = useCallback((integration: IntegrationListItem) => {
    setEditing(integration);
  }, []);

  const handleClose = useCallback(() => {
    setEditing(null);
  }, []);

  return (
    <PageContainer enableScroll={false} className="flex flex-col h-full gap-4 w-full">
      <h1 className="text-xl font-semibold">{t('integrations:routes.all')}</h1>
      <AwaitContainer isLoading={isLoading} className="flex-1 overflow-hidden">
        {data && data.items.length === 0 ? (
          <div className="text-muted-foreground p-4">{t('integrations:table.empty')}</div>
        ) : (
          <IntegrationsTable
            items={data?.items ?? []}
            total={data?.total ?? 0}
            page={page}
            perPage={PER_PAGE}
            onChangePagination={handleChangePagination}
            onEdit={handleEdit}
          />
        )}
      </AwaitContainer>
      <EditIntegrationDialog integration={editing} isOpen={editing !== null} onClose={handleClose} />
    </PageContainer>
  );
};
